import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import type { DtaListedAccount, FiduciarieTableColumns } from 'azshared';
import { Fragment, KeyboardEvent, MouseEvent, useCallback } from 'react';
import { MessageDescriptor } from 'react-intl';
import { AppSvgIcon } from '../../../../components/svg-icon/app-svg-icon';

export interface FiduciarieResultsColumn {
  key: FiduciarieTableColumns;
  label: MessageDescriptor;
  value: (account: DtaListedAccount) => string | undefined;
}

// Shown for the fields the server leaves empty.
const EMPTY_VALUE = '-';

interface FiduciarieResultsRowProps {
  account: DtaListedAccount;
  columns: FiduciarieResultsColumn[];
  onDelete?: (account: DtaListedAccount) => void;
  onOpen?: (account: DtaListedAccount) => void;
}

export const FiduciarieResultsRow = (props: FiduciarieResultsRowProps) => {
  const { account, columns, onDelete, onOpen } = props;

  const handleDelete = useCallback(
    (e: MouseEvent<SVGSVGElement> | KeyboardEvent<SVGSVGElement>) => {
      // The icon sits inside the row: the click must not open the detail too.
      e.stopPropagation();
      onDelete?.(account);
    },
    [account, onDelete]
  );

  const handleOpen = useCallback(() => {
    onOpen?.(account);
  }, [account, onOpen]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTableRowElement>) => {
      if (e.key === 'Enter') {
        handleOpen();
      }
    },
    [handleOpen]
  );

  return (
    <Fragment>
      <TableRow
        className={`results-table-row${onOpen ? ' clickable-row' : ''}`}
        hover={!!onOpen}
        tabIndex={onOpen ? 0 : undefined}
        onClick={handleOpen}
        onKeyDown={handleKeyDown}>
        {columns.map((column, index) =>
          index === 0 ? (
            <TableCell
              key={column.key}
              className='results-table-row-cell'
              component='th'
              scope='row'
              sx={{
                borderBottomLeftRadius: 8,
                borderTopLeftRadius: 8
              }}>
              {column.value(account) || EMPTY_VALUE}
            </TableCell>
          ) : (
            <TableCell key={column.key} className='results-table-row-cell' align='left'>
              {column.value(account) || EMPTY_VALUE}
            </TableCell>
          )
        )}
        <TableCell
          className='results-table-row-cell action-cell'
          align='center'
          sx={{
            borderBottomRightRadius: 8,
            borderTopRightRadius: 8,
            padding: '8px 16px',
            textAlign: 'center'
          }}>
          <div
            className='actions-wrapper'
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '20px'
            }}>
            <AppSvgIcon icon={'trash'} onClick={handleDelete} className='action-icon delete-icon' />
          </div>
        </TableCell>
      </TableRow>
    </Fragment>
  );
};
